import BaseListStore from 'stores/BaseListStore';
import CommentStore, { IComment } from 'stores/comment/CommentStore';
import { action, observable, makeObservable, runInAction } from 'mobx';
import { destroy, get, patch, post } from 'stores/_services/dataService';
import log from 'services/helpers/log';

export default class CommentListStore extends BaseListStore<CommentStore> {
    commentsIdArray: Record<string, string[]> = {};

    constructor() {
        super({
            itemStore: CommentStore
        });

        makeObservable(this, {
            commentsIdArray: observable,
            getComments: action,
            saveComment: action,
            addComment: action,
            starComment: action,
            deleteComment: action
        });
    }

    findComments(relatedId: string): IComment[] {
        return this.commentsIdArray[relatedId]?.map((id) => this.data.get(id)?.comment) || [];
    }

    async getComments(relatedId: string): Promise<void> {
        const options = {
            excludeFields: 'relatedId',
            filter: {
                where: { relatedId },
                order: ['starred DESC', 'starredOn DESC', 'createdOn DESC'],
                include: {
                    relation: 'author',
                    scope: {
                        fields: ['email']
                    }
                }
            }
        };

        const { data } = await get('userComments', options);

        if (!this.commentsIdArray[relatedId]) {
            //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
            runInAction(() => {
                this.commentsIdArray[relatedId] = [];
            });
        }

        this._updateData(data);
        this._updateIds(data, 0, this.commentsIdArray[relatedId]);
    }

    async saveComment(comment: IComment, value: string): Promise<void> {
        await patch(`userComments/${comment.id}`, { value });
        this._updateData({ ...comment, value });
    }

    async addComment(relatedId: string, value: string): Promise<void> {
        if (!relatedId) {
            log.error('Add comment requires a relatedId');
            return;
        }

        await post('userComments', { value, relatedId });
    }

    async starComment(comment: IComment): Promise<void> {
        const { id, starred } = comment;

        this._updateData({ ...comment, starred: !starred });

        try {
            await patch(`userComments/${id}`, { starred: !starred });
        } catch (e) {
            this._updateData({ ...comment, starred: !starred });
        }
    }

    async deleteComment(relatedId: string, id: string): Promise<void> {
        if (!id) {
            throw new Error('ID required to delete comment');
        }

        await destroy(`userComments/${id}`);

        this.clearComment(relatedId, id);
    }

    private clearComment(relatedId: string, id: string) {
        this.commentsIdArray[relatedId] = this.commentsIdArray[relatedId].filter((i) => i !== id);
        this.data.delete(id);
    }
}
